import { useEffect, useState, type DragEvent } from 'react';
import { ImagePlus, X } from 'lucide-react';
import { Alert } from './ui/Alert';
import { Button, buttonStyles } from './ui/Button';
import type { Job } from '../types/models';
import { uploadJobImage, type WebImageType } from '../lib/imageUpload';
import { evidenceUploadTypes } from '../lib/evidence';
import { PhotoTypeTag } from './JobTags';

interface Props {
  companyId: string;
  job: Job;
  disabled?: boolean;
  onUploaded?: () => void;
}

export function PhotoUploader({ companyId, job, disabled, onUploaded }: Props) {
  const [imageType, setImageType] = useState<WebImageType>('collection');
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const imageTypes = evidenceUploadTypes(job.courtesyCarRequired === true);
  const allowedType = imageTypes.some((type) => type.value === imageType) ? imageType : 'additional';

  useEffect(() => {
    if (!job.courtesyCarRequired) setImageType((current) => current === 'courtesy' ? 'additional' : current);
  }, [job.courtesyCarRequired]);

  async function uploadSelected() {
    if (disabled || uploading || files.length === 0) return;
    const selectedFiles = files;
    setUploading(true);
    setError(null);
    setDone(null);
    try {
      for (let index = 0; index < selectedFiles.length; index += 1) {
        const file = selectedFiles[index];
        setProgress({ done: index, total: selectedFiles.length });
        await uploadJobImage({
          companyId,
          jobId: job.id,
          file,
          imageType: allowedType,
        });
        setFiles((current) =>
          current.filter((candidate) => fileKey(candidate) !== fileKey(file))
        );
      }
      setProgress(null);
      setDone(
        `${selectedFiles.length} photo${selectedFiles.length === 1 ? '' : 's'} uploaded.`
      );
      onUploaded?.();
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  }

  function addFiles(nextFiles: File[]) {
    const imageFiles = nextFiles.filter((file) => file.type.startsWith('image/'));
    if (imageFiles.length === 0) return;
    setFiles((current) => {
      const existing = new Set(current.map(fileKey));
      return [
        ...current,
        ...imageFiles.filter((file) => !existing.has(fileKey(file))),
      ];
    });
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setIsDragging(false);
    if (disabled || uploading) return;
    addFiles(Array.from(event.dataTransfer.files));
  }

  return (
    <div
      onDragEnter={(event) => { event.preventDefault(); if (!disabled && !uploading) setIsDragging(true); }}
      onDragOver={(event) => { event.preventDefault(); if (!disabled && !uploading) setIsDragging(true); }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      className={`rounded-[12px] border-[1.5px] border-dashed px-3.5 py-3 transition-colors duration-[180ms] ease-out ${
        isDragging ? 'border-accent bg-accenttint' : 'border-strong'
      }`}
    >
      <div className="flex flex-wrap items-center gap-2">
        {/* The heading is chosen before the photographs are filed, as on the phone. */}
        <div role="radiogroup" aria-label="Evidence heading" className="flex flex-wrap gap-1.5">
          {imageTypes.map((type) => {
            const selected = allowedType === type.value;
            return (
              <button
                key={type.value}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={disabled || uploading}
                onClick={() => setImageType(type.value)}
                className={`rounded-full p-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50 ${selected ? 'ring-2 ring-accent ring-offset-1 ring-offset-elev' : ''}`}
              >
                <PhotoTypeTag type={type.value} label={type.label} />
              </button>
            );
          })}
        </div>

        <div className="ml-auto flex items-center gap-2">
          {/* Keep the clipped picker inside this button, not the outer document. */}
          <label className={`relative ${buttonStyles('secondary', 'sm')} h-8 cursor-pointer px-3 text-[13.5px] focus-within:ring-2 focus-within:ring-accent ${disabled || uploading ? 'pointer-events-none opacity-50' : ''}`}>
            Choose
            <input
              type="file"
              accept="image/*"
              multiple
              disabled={disabled || uploading}
              className="sr-only left-0 top-0"
              onChange={(event) => { addFiles(Array.from(event.target.files ?? [])); event.target.value = ''; }}
            />
          </label>
          <Button type="button" size="sm" className="h-8 px-3 text-[13.5px]" loading={uploading}
            disabled={disabled || uploading || files.length === 0} onClick={uploadSelected}>
            {files.length > 0 ? `Upload ${files.length}` : 'Upload'}
          </Button>
        </div>
      </div>

      <p className="mt-2 text-[12.5px] text-ink3">
        <ImagePlus size={13} strokeWidth={1.9} className="mr-1 inline align-[-2px] text-accent" aria-hidden />
        Drop photographs here — filed under {imageTypes.find((type) => type.value === allowedType)?.label ?? 'Additional'} and verified by the server.
      </p>

      {files.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {files.map((file) => (
            <li key={fileKey(file)}
              className="inline-flex max-w-full items-center gap-1 rounded-[7px] bg-search py-0.5 pl-2 pr-0.5 text-[12.5px] text-ink2">
              <span className="max-w-44 truncate">{file.name}</span>
              <button
                type="button"
                title={`Remove ${file.name}`}
                aria-label={`Remove ${file.name}`}
                disabled={uploading}
                onClick={() => setFiles((current) => current.filter((candidate) => fileKey(candidate) !== fileKey(file)))}
                className="flex h-5 w-5 items-center justify-center rounded-[5px] text-ink3 hover:bg-hover hover:text-ink disabled:opacity-50"
              >
                <X size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Determinate wherever a count exists — never a spinner where a number is known. */}
      {progress && (
        <div className="mt-2.5">
          <div className="flex items-center justify-between text-[12.5px] text-ink2">
            <span>Uploading</span>
            <span className="font-mono">{progress.done + 1} / {progress.total}</span>
          </div>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-sunken">
            <div className="h-full rounded-full bg-st-progress transition-[width] duration-[180ms] ease-out"
              style={{ width: `${(progress.done / progress.total) * 100}%` }} />
          </div>
        </div>
      )}
      {done && <p className="mt-2 text-[12.5px] font-medium text-success-ink">{done}</p>}
      {error && <div className="mt-2"><Alert tone="error">{error}</Alert></div>}
    </div>
  );
}

function fileKey(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`;
}
